import { ReactElement, useContext, useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  FormControlLabel,
  IconButton,
  InputLabel, MenuItem,
  Paper, Switch,
  Tooltip, useMediaQuery
} from "@mui/material";
import { AddToQueue, Dashboard, FolderOpen, RemoveFromQueue, Save } from "@mui/icons-material";
import MainDialog from "./mainDialog";
import { useAsyncFetch } from "../../../system/useFetch";
import { IResponseData, ISelectedNode, IState, Modules } from "../../../types";
import { IContext, IDashboard, IDashboardRealtimeConfig } from "../types";
import { useDispatch, useSelector } from "react-redux";
import { SystemContext } from "../../../context/systemContext";
import OpenDashboard from "./openDashboard";
import { getEmptyDashboard } from "./utils";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { getPalette } from "../../../configuration/configuration";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import BrushIcon from "@mui/icons-material/Brush";
import { DateTime } from "luxon";
import { LocalizationProvider } from "@mui/x-date-pickers";
import SyncIcon from "@mui/icons-material/Sync";
import { addAlert } from "../../../actions";
import PaletteDialog from "./paletteDialog";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import UndoIcon from "@mui/icons-material/Undo";
import { cloneObject } from "../../../system/utils";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import FileCopyIcon from "@mui/icons-material/FileCopy";

interface IProps {
  dashboard: IDashboard;

  context: IContext;
  setContext: (cxt: IContext, newDashboard: boolean) => void;

  onOpenDashboard: (dashboard: IDashboard) => void;
  onUpdate: () => void;
}


let timer: NodeJS.Timeout | null = null;

const resetTimer = () => {
  if(timer) {
    clearInterval(timer);
    timer = null;
  }
};

const Toolbar = ({ dashboard, context, setContext, onOpenDashboard, onUpdate }: IProps): ReactElement => {
  const dispatch = useDispatch<any>();
  const disableAccordion = useMediaQuery("(min-width:1500px)");
  const selectedNode = useSelector<IState, ISelectedNode>( state => state.selectedNode);
  const systemContext  = useContext(SystemContext);
  const [ open, setOpen ] = useState(false);
  const [ openDialog, setOpenDialog ] = useState(false);
  const [ openPaletteDialog, setOpenPaletteDialog ] = useState(false);
  const [ executeRequest ]  = useAsyncFetch<IResponseData<number>>();
  const [ moveFactor, setMoveFactor ] = useState(1);
  const [ pending, setPending ] = useState(false);

  const [ realtime, setRealtime ] = useState<IDashboardRealtimeConfig>(dashboard.customConfig.autoUpdate);
  const [ subArea, setSubArea ] = useState(0);

  useEffect(() => {
    if (pending) {
      onUpdate();
      setPending(false);
    }
  }, [ pending ]);

  useEffect(() => {
    if (dashboard.customConfig.autoUpdate.enabled !== realtime.enabled ||
      dashboard.customConfig.autoUpdate.scan !== realtime.scan) {
      setRealtime(context.dashboard!.customConfig.autoUpdate);
    }
  }, [ dashboard ]);

  const resetRange = () => {
    const span = context.dashboard!.customConfig.range;
    const now = DateTime.now();

    setContext({
      ...context,
      from: now.plus( { hour: -span }),
      to: now,
      factoryDay: now
    }, false);

    setPending(true);
  };

  useEffect(() => {
    resetTimer();
    if (realtime.enabled) {
      resetRange();
      timer = setInterval(resetRange, realtime.scan * 1000);
    }

    return resetTimer;
  }, [ realtime ]);

  const updateAutoUpdateData = (field: keyof IDashboardRealtimeConfig, value: any) => {
    const newData = cloneObject(realtime) as any;

    newData[field] = value as any;
    setContext( { ...context, dashboard: { ...context.dashboard!, customConfig: { ...dashboard.customConfig, autoUpdate: newData } } }, false );
  };

  const backward = () => {
    const newContext = { ...context, factoryDay: context.factoryDay.minus( { days: 1 } ) };
    if (context.dashboard!.customConfig.autoUpdate.enabled)  {
      const autoUpdate = cloneObject(realtime) as any;
      autoUpdate.enabled = false;
      newContext.dashboard!.customConfig.autoUpdate = autoUpdate;
    }

    setContext(newContext, false);
  };


  const forward = () => {
    const newContext = { ...context, factoryDay: context.factoryDay.plus( { days: 1 } )  };

    if (context.dashboard!.customConfig.autoUpdate.enabled)  {
      const autoUpdate = cloneObject(realtime) as any;
      autoUpdate.enabled = false;
      newContext.dashboard!.customConfig.autoUpdate = autoUpdate;
    }

    setContext(newContext, false);
  };

  const handleDateChange = (date: any) => {
    if (date && date.isValid) {
      setContext({ ...context, factoryDay: date }, false);
    }
  };

  const handleFromDateChange = (date: any) => {
    if (date && date.isValid) {
      if (date > context.to) {
        setContext({ ...context, from: context.to, to: date }, false);

      } else  {
        setContext({ ...context, from: date }, false);
      }
    }
  };

  const handleToDateChange = (date: any) => {
    if (date && date.isValid) {
      if (date < context.from) {
        setContext({ ...context, from: date, to: context.from }, false);

      } else  {
        setContext({ ...context, to: date }, false);
      }
    }
  };

  const onSave = () => {
    executeRequest({
      module: "dashboard",
      query: "saveDashboard",
      payload: { dashboard: {
        ...dashboard,
        moduleID: Modules.INTERRUPTIONS,
        nodeType: selectedNode.type,
        nodeID: selectedNode.nodeID,
        userID: systemContext.user?.userID ?? 0
      } }
    })
      .then(response => {
        if (response?.status && response.data) {
          setContext({ ...context,
            dashboard: {
              ...context.dashboard!,
              dashboardID: response.data ?? null
            }
          }, false);
          dispatch(addAlert({ message: "The layout was saved!", severity: "success", hash: undefined }));
        }
      });
  };

  const onNewDashboard = () => {
    setContext({ ...context, dashboard: getEmptyDashboard() }, true);
  };

  const onCloneDashboard = () => {
    systemContext.showMessage({
        title: "Clone Layout",
        color: "info",
        message: "Do you want to clone the Layout?",
        callback: () => {
          const cloned = cloneObject(context.dashboard!);
          cloned.dashboardID = null;
          cloned.name = `${cloned.name} - cloned`;

          setContext({ ...context, dashboard: cloned }, true);
        }
      });
  };

  const onUpdateInternal = () =>{
    updateAutoUpdateData("enabled", false);
    onUpdate();
  };

  const editAllowed = dashboard.userID === 0 || systemContext.user?.userID === dashboard.userID || dashboard.isEditable;

  const onRemoveDashboard = () => {
    systemContext.showMessage({
        title: "Delete Dashboard",
        color: "danger",
        message: "Do you want to delete the Layout?",
        callback: () =>{
          executeRequest({
            module: "dashboard",
            query: "deleteDashboard",
            payload: { dashboardID: dashboard.dashboardID }
          })
            .then(response => {
              if (response?.status) {
                dispatch(addAlert({ message: "The layout was deleted!", severity: "error", hash: undefined }));
                onNewDashboard();
              }
            });
        }
      });
  };

  const updateMoveFactor = (forwardDirection: boolean) => {
    const span = (forwardDirection ? 1 : -1) * (context.to.toSeconds() - context.from.toSeconds()) * moveFactor;
    setContext( { ...context, from: context.from.plus( { second: span }), to: context.to.plus( { second: span }) }, false);
    setPending(true);
  };

  const onUpdateDashboard = (newDashboard: IDashboard) => {
    setContext({ ...context, dashboard: newDashboard }, false);
  };


  return (
    <Paper className="p-1 mb-1 display-flex-row align-items-center">
      {/*<div className=" ">*/}
      { !disableAccordion &&
        <strong onClick={() => setSubArea(0)}
          style={{ cursor: "pointer",  opacity: subArea !== 0 ? 0.5 : 1.0, marginRight: ".5em", transform: "rotate(-90deg)" }}>
          Menu
        </strong>}
      { (disableAccordion || subArea === 0) &&
          <>
            <IconButton color="primary" onClick={() => setOpenDialog(true)}>
              <Tooltip title={"Open"}>
                <FolderOpen/>
              </Tooltip>
            </IconButton>
            <IconButton color="success" onClick={onSave}>
              <Tooltip title={"Save"}>
                <Save/>
              </Tooltip>
            </IconButton>
            <IconButton color="primary" onClick={onCloneDashboard} >
              <Tooltip title={"Clone Layout"}>
                <FileCopyIcon/>
              </Tooltip>
            </IconButton>
            <IconButton color="primary" onClick={onNewDashboard} >
              <Tooltip title={"New Layout"}>
                <AddToQueue/>
              </Tooltip>
            </IconButton>
            <IconButton color="error" disabled={!dashboard.dashboardID} onClick={onRemoveDashboard}>
              <Tooltip title={"Delete Layout"}>
                <RemoveFromQueue/>
              </Tooltip>
            </IconButton>
            <IconButton color="primary" onClick={() => setOpen(true)} disabled={!editAllowed}>
              <Tooltip title={"Layout"}>
                <Dashboard/>
              </Tooltip>
            </IconButton>
            <IconButton color="primary" onClick={() => setOpenPaletteDialog(true)} disabled={!editAllowed}>
              <Tooltip title={"Palette"}>
                <BrushIcon/>
              </Tooltip>
            </IconButton>
          </>
      }

      {!dashboard.isAcquisition && !disableAccordion &&
        <strong onClick={() => setSubArea(1)}
          style={{ cursor: "pointer", opacity: subArea !== 1 ? 0.5 : 1.0, marginLeft: ".5em", marginRight: ".5em", transform: "rotate(-90deg)" }} >
          Day
        </strong> }
      {!dashboard.isAcquisition && (disableAccordion || subArea === 1) &&

      // format={getDefaultLocaleConfig().dateFormat}

        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <div className="display-flex-row flex-grow-0 align-items-center mr-2" style={{ display: "inline-flex" }}>
            <InputLabel className="ml-2 mr-1">{systemContext.language.factoryDay}</InputLabel>
            <IconButton color="primary" onClick={backward} size={"small"} style={{ width: 25, height: 25 }}>
              <KeyboardArrowLeftIcon fontSize="inherit" fontWeight="bold"/>
            </IconButton>
            <DatePicker
              value={context.factoryDay}
              maxDate={DateTime.now()}
              onChange={handleDateChange} />
            <IconButton color="primary" onClick={forward} size={"small"} style={{ width: 25, height: 25 }}>
              <KeyboardArrowRight fontSize="inherit" fontWeight="bold"/>
            </IconButton>
          </div>
        </LocalizationProvider>
      }

      {dashboard.isAcquisition && !disableAccordion &&
        <strong onClick={() => setSubArea(1)}
          style={{ cursor: "pointer", opacity: subArea !== 1 ? 0.5 : 1.0, marginLeft: ".5em", marginRight: ".5em", transform: "rotate(-90deg)" }} >
          Range
        </strong>
      }

      {/*format={`${getDefaultLocaleConfig().dateFormat} ${getDefaultLocaleConfig().timeFormat.replace(":ss", "")}`}*/}

      {dashboard.isAcquisition && (disableAccordion || subArea === 1) &&
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <div className="display-flex-row flex-grow-0 align-items-center mr-2" style={{ display: "inline-flex" }}>
            <InputLabel className="ml-2 mr-1">From</InputLabel>
            <DateTimePicker
              maxDate={DateTime.now()}
              value={context.from}
              onChange={handleFromDateChange} />

            <InputLabel className="ml-2 mr-1">To</InputLabel>
            <DateTimePicker
              maxDate={DateTime.now()}
              value={context.to}
              onChange={handleToDateChange} />
          </div>
        </LocalizationProvider>
      }

      {dashboard.isAcquisition && !disableAccordion &&
        <strong onClick={() => setSubArea(2)}
          style={{ cursor: "pointer", opacity: subArea !== 2 ? 0.5 : 1.0, marginLeft: ".5em", marginRight: ".5em", transform: "rotate(-90deg)" }} >
          Move
        </strong> }

      {dashboard.isAcquisition && (disableAccordion || subArea === 2) &&
        <>
          <IconButton color="primary" onClick={() => updateMoveFactor(false)}  size="small">
            <Tooltip title={"Backward"}>
              <ArrowBackIosIcon/>
            </Tooltip>
          </IconButton>

          <ButtonGroup size="small" aria-label="small outlined button group">
            <Button style={{ background: moveFactor === .25 ? getPalette().statusColor.selected.background : undefined }}
              onClick={() => setMoveFactor(.25)}>
              1&frasl;4
            </Button>
            <Button style={{ background: moveFactor === .5 ? getPalette().statusColor.selected.background : undefined }}
              onClick={() => setMoveFactor(.5)}>
              1&frasl;2
            </Button>
            <Button style={{ background: moveFactor === 1 ? getPalette().statusColor.selected.background : undefined }}
              onClick={() => setMoveFactor(1)}>
              1
            </Button>
            <Button onClick={resetRange}>
              <Tooltip title={"Reset"}>
                <UndoIcon/>
              </Tooltip>
            </Button>
          </ButtonGroup>

          <IconButton color="primary" onClick={() => updateMoveFactor(true)} size="small">
            <Tooltip title={"Forward"}>
              <ArrowForwardIosIcon/>
            </Tooltip>
          </IconButton>
        </>
      }

      {!disableAccordion &&
        <strong onClick={() => setSubArea(3)}
          style={{ cursor: "pointer", opacity: subArea !== 3 ? 0.5 : 1.0, marginLeft: ".5em", marginRight: ".5em", transform: "rotate(-90deg)" }}>
          Update
        </strong>}
      {(disableAccordion || subArea === 3) &&
          <>
            <IconButton color="primary" onClick={onUpdateInternal} className="ml-2">
              <Tooltip title={"Update data"}>
                <SyncIcon/>
              </Tooltip>
            </IconButton>
            <InputLabel>{systemContext.language.update}</InputLabel>

            <FormControlLabel
              style={{ marginLeft: "1em" }}
              control={<Switch checked={realtime.enabled}
                onChange={() => updateAutoUpdateData("enabled", !realtime.enabled)}
                name="isAcquisition"/>}
              label="Auto update"
            />

            <Select
              variant="standard"
              value={`${realtime.scan}`}
              displayEmpty={true}
              onChange={(event: SelectChangeEvent) => updateAutoUpdateData("scan", parseInt(event.target.value, 10))}>
              <MenuItem value={60}>1 min</MenuItem>
              <MenuItem value={300}>5 min</MenuItem>
              <MenuItem value={600}>10 min</MenuItem>
            </Select>

          </>
      }

      <div className="flex-grow-1" />
      <strong className="mr-2" style={{ fontSize: "1.2em" }}>{dashboard.name}</strong>
      {/*</div>*/}

      <MainDialog dashboard={dashboard} setDashboard={onUpdateDashboard} open={open} setOpen={setOpen} />
      <OpenDashboard selectedNode={selectedNode} open={openDialog} onClose={() => setOpenDialog(false)} onOpenDashboard={onOpenDashboard}/>
      <PaletteDialog  dashboard={dashboard} setDashboard={onUpdateDashboard} open={openPaletteDialog} setOpen={setOpenPaletteDialog} />
    </Paper>
  );
};

export default Toolbar;
